import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import type { DocumentModel } from "@/models/DocumentModel";

type Options = {
  doc: DocumentModel;
};

type PluginState = {
  decorations: DecorationSet | null;
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    highlightSuggestedText: {
      setInputPosRange: (range: { from: number; to: number }) => ReturnType;
    };
  }
}

const highlightSuggestedTextKey = new PluginKey<PluginState>("highlightSuggestedText");

export const HighlightSuggestedText = Extension.create<Options>({
  name: "highlightSuggestedText",

  addProseMirrorPlugins() {
    const { doc } = this.options;

    return [
      new Plugin<PluginState>({
        key: highlightSuggestedTextKey,
        state: {
          init: () => ({ decorations: null }),
          apply(tr, prev) {
            if (tr.getMeta("inputPosRange")) {
              const range = tr.getMeta("inputPosRange") as { from: number; to: number };
              let { decorations } = prev;

              if (range.from !== -1 && range.to !== -1) {
                decorations = DecorationSet.create(tr.doc, [
                  Decoration.inline(range.from, range.to, {
                    class: "underline decoration-blue-500",
                    spellcheck: "false",
                  }),
                ]);
              } else {
                decorations = null;
              }

              if (doc.editor.editorState.latexInput) {
                decorations = null;
              }

              return {
                ...range,
                decorations,
              };
            }

            return prev;
          },
        },
        props: {
          decorations(state) {
            if (!doc.editor.suggestions.visible) return null;

            return this.getState(state)?.decorations ?? null;
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      setInputPosRange:
        (range: { from: number; to: number }) =>
        ({ commands }) => {
          return commands.setMeta("inputPosRange", range);
        },
    };
  },
});
